import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ScrollableCategoryTabs from '../components/ScrollableCategoryTabs';
import LetterIndex from '../components/browse/LetterIndex';
import EntityList from '../components/browse/EntityList';
import { SearchIcon, GlobeIcon } from '../components/Icons';
import TopBarSlot from '../components/TopBarSlot';
import { getCategoryIcon, getCategoryLabel } from '../constants/categoryDefinitions';
import './BrowsePage.css';

// ── Category definitions ──────────────────────────────────────────
const ALL_KEY = 'all';

const BROWSE_KEYS = ['organization', 'signer', 'unit', 'company', 'companyperson', 'afmentity'];

const CATEGORIES = [
  { key: ALL_KEY, label: 'All', icon: React.createElement(GlobeIcon, { size: 14 }) },
  ...BROWSE_KEYS.map((key) => ({
    key,
    label: getCategoryLabel(key),
    icon: getCategoryIcon(key, 14),
  })),
];

// ── Component ────────────────────────────────────────────────────

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL (bookmarkable + back-button friendly)
  const entityType = searchParams.get('type') || ALL_KEY;
  const letter = searchParams.get('letter') || null;
  const sort = searchParams.get('sort') || 'asc';

  // Local state
  const [prefixQuery, setPrefixQuery] = useState('');
  const [availableLetters, setAvailableLetters] = useState([]);
  const [scrollToLetter, setScrollToLetter] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── Update URL when filters change ─────────────────────────────
  const updateParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value && value !== ALL_KEY) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleTypeChange = useCallback(
    (key) => {
      updateParam('type', key);
    },
    [updateParam]
  );

  const handleSortToggle = useCallback(() => {
    updateParam('sort', sort === 'asc' ? 'desc' : 'asc');
  }, [sort, updateParam]);

  // ── Letter click from sidebar ──────────────────────────────────
  const handleLetterClick = useCallback(
    (clickedLetter) => {
      // If we already have results for this letter, just scroll
      if (availableLetters.includes(clickedLetter)) {
        setScrollToLetter(clickedLetter);
      }
      // Also update the URL so the letter filter is applied
      updateParam('letter', clickedLetter);
    },
    [availableLetters, updateParam]
  );

  // ── Prefix search handler ──────────────────────────────────────
  const handlePrefixSearch = useCallback((e) => {
    setPrefixQuery(e.target.value);
  }, []);

  // Clear scroll target after it's been consumed
  useEffect(() => {
    if (scrollToLetter) {
      const timeout = setTimeout(() => setScrollToLetter(null), 500);
      return () => clearTimeout(timeout);
    }
  }, [scrollToLetter]);

  // ── Build category tabs with dynamic counts ────────────────────
  // (Counts are shown inside EntityList's letter headers, but here
  //  we keep zero-count categories visible so users can still select
  //  them — they'll just see "No entities found" for that type.)
  const tabCategories = useMemo(() => {
    return CATEGORIES.map((cat) => ({
      ...cat,
      count: 0, // Total count is available from API but not needed for tabs
      visible: true,
    }));
  }, []);

  // ── Keyboard shortcut: "/" focuses the search input ────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === '/' &&
        document.activeElement !== searchInputRef.current &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="browse-page">
      {/* ── Top bar rendered into the fixed header via portal ──── */}
      <TopBarSlot>
        <div className="browse-top-bar-inline">
          <div className="browse-tabs-desktop">
            <ScrollableCategoryTabs
              categories={tabCategories}
              selectedKey={entityType}
              onSelect={handleTypeChange}
            />
          </div>

          <div className="browse-search-desktop">
            <div className="browse-controls">
              {/* Prefix search */}
              <div className="browse-search-wrapper">
                <SearchIcon size={16} className="browse-search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="browse-search-input"
                  placeholder="Filter by prefix (e.g. Tes)…"
                  value={prefixQuery}
                  onChange={handlePrefixSearch}
                  aria-label="Filter entities by prefix"
                />
                {isSearching && prefixQuery && (
                  <div className="browse-search-spinner" />
                )}
                {prefixQuery && (
                  <button
                    className="browse-search-clear"
                    onClick={() => setPrefixQuery('')}
                    aria-label="Clear filter"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Sort direction toggle */}
              <button
                className="browse-sort-btn"
                onClick={handleSortToggle}
                title={`Sort ${sort === 'asc' ? 'ascending' : 'descending'}`}
                aria-label={`Sort ${sort === 'asc' ? 'ascending' : 'descending'}`}
              >
                {sort === 'asc' ? 'A→Z' : 'Z→A'}
              </button>
            </div>
          </div>
        </div>
      </TopBarSlot>

      {/* ── Category tabs for narrow screens (moved out of the top ribbon) ── */}
      <div className="browse-tabs-mobile">
        <ScrollableCategoryTabs
          categories={tabCategories}
          selectedKey={entityType}
          onSelect={handleTypeChange}
        />
      </div>

      {/* ── Search controls for narrow screens ──────────────────── */}
      <div className="browse-search-mobile">
        <div className="browse-controls">
          <div className="browse-search-wrapper">
            <SearchIcon size={16} className="browse-search-icon" />
            <input
              type="text"
              className="browse-search-input"
              placeholder="Filter by prefix…"
              value={prefixQuery}
              onChange={handlePrefixSearch}
              aria-label="Filter entities by prefix"
            />
            {isSearching && prefixQuery && (
              <div className="browse-search-spinner" />
            )}
            {prefixQuery && (
              <button
                className="browse-search-clear"
                onClick={() => setPrefixQuery('')}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
          <button
            className="browse-sort-btn"
            onClick={handleSortToggle}
            title={`Sort ${sort === 'asc' ? 'ascending' : 'descending'}`}
            aria-label={`Sort ${sort === 'asc' ? 'ascending' : 'descending'}`}
          >
            {sort === 'asc' ? 'A→Z' : 'Z→A'}
          </button>
        </div>
      </div>

      {/* ── Main layout: letter sidebar + entity list ────────────── */}
      <div className="browse-layout" ref={scrollContainerRef}>
        <LetterIndex
          availableLetters={availableLetters}
          scrollContainerRef={scrollContainerRef}
          onLetterClick={handleLetterClick}
        />

        <div className="browse-content">
          {/* Active filters indicator */}
          {(letter || prefixQuery) && (
            <div className="browse-active-filters">
              {letter && (
                <span className="browse-filter-chip">
                  Letter: <strong>{letter}</strong>
                  <button
                    onClick={() => updateParam('letter', null)}
                    aria-label="Remove letter filter"
                  >
                    ✕
                  </button>
                </span>
              )}
              {prefixQuery && (
                <span className="browse-filter-chip">
                  Prefix: <strong>"{prefixQuery}"</strong>
                  <button
                    onClick={() => setPrefixQuery('')}
                    aria-label="Remove prefix filter"
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          )}

          <EntityList
            entityType={entityType}
            letter={letter}
            query={prefixQuery}
            sort={sort}
            onLettersLoaded={setAvailableLetters}
            onLoadingChange={setIsSearching}
            scrollToLetter={scrollToLetter}
          />
        </div>
      </div>

      {/* ── Keyboard hint ──────────────────────────────────────── */}
      <div className="browse-keyboard-hint">
        Press <kbd>/</kbd> to focus the search
      </div>
    </div>
  );
};

export default BrowsePage;
